import React, { useCallback, useMemo } from 'react';
import styles from './GeneratedSceneItem.module.scss';
import TextComponent from './TextComponent';
import { useTranslation } from 'react-i18next';
import NameEditComponent from '../NameEditComponent/NameEditComponent';
import cx from 'classnames';
import RadioButton from '../RadioButton/RadioButton';

const GeneratedSceneItem = ({
  item,
  assistantItem = null,
  onChange,
  userPermissions,
  comparisonMode = false,
  assistantSelected = {},
  onSelectOption = () => { },
  isCollapsed,
}) => {
  const { t } = useTranslation();
  const disableEditing = useMemo(() => item?.processing || !userPermissions.updateStory || comparisonMode, [
    item,
    userPermissions,
    comparisonMode,
  ]);

  const showDifference = useMemo(() => {
    if (comparisonMode && assistantItem) {
      return {
        name: assistantItem?.name !== item?.name,
        script: assistantItem?.script !== item?.script,
        narrative: assistantItem?.narrative !== item?.narrative,
      };
    }
    return null;
  }, [comparisonMode, assistantItem]);

  const handleNameChange = value => {
    if (value.trim().length > 0) {
      onChange(item?.id, { name: value });
    }
  };

  const handleNarrativeChanged = value => {
    onChange(item?.id, { narrative: value });
  };

  const handleScriptChanged = value => {
    onChange(item?.id, { script: value });
  };

  const handleSelectOriginal = (key = null) => e => {
    onSelectOption(assistantItem?.id ?? item.id, key, !e.currentTarget.checked);
  };

  const handleSelectAssistant = (key = null) => e => {
    onSelectOption(assistantItem?.id ?? item.id, key, e.currentTarget.checked);
  };

  return (
    <div className={styles.wrapper} id={`${item?.id}`}>
      {comparisonMode && !item && (
        <div className={cx(styles.contentWrapper, styles.noContent)}>
          <RadioButton
            variant="primary"
            checked={assistantSelected === null}
            onChange={handleSelectOriginal(null)}
            className={styles.radioBtn}
          />
          <p>{t('storyboard.editor.assistant.old_scene_selected')}</p>
        </div>
      )}
      <div className={styles.contentWrapper}>
        <div className={cx(styles.headerContainer, { [styles.decreaseMargin]: comparisonMode && item?.video })}>
          {item && (
            <div className={styles.nameWithSelection}>
              {(showDifference?.name || (comparisonMode && !assistantItem)) && (
                <RadioButton
                  variant="primary"
                  checked={assistantSelected && !assistantSelected?.name}
                  onChange={handleSelectOriginal(assistantItem ? 'name' : null)}
                  className={styles.radioBtn}
                />
              )}
              <NameEditComponent
                size="small"
                name={item?.name}
                handleSaveName={handleNameChange}
                disableEditing={disableEditing}
                className={styles.header}
                isEditIcon={true}
              />
            </div>
          )}
          {showDifference?.name && (
            <div className={styles.nameWithSelection}>
              <RadioButton
                variant="secondary"
                checked={Boolean(assistantSelected?.name)}
                onChange={handleSelectAssistant(item ? 'name' : null)}
                className={styles.radioBtn}
              />
              <NameEditComponent
                size="small"
                name={assistantItem?.name}
                disableEditing={true}
                className={styles.header}
                variant="secondary"
              />
            </div>
          )}
        </div>
        {comparisonMode && item?.video && (
          <p className={styles.recordedWarning}>{`(${t('storyboard.editor.assistant.recorded_warning')})`}</p>
        )}
        <div className={styles.container}>
          {item && (
            <div className={styles.containerWithSelection}>
              <TextComponent
                title={t('storyboard.editor.narrative_name')}
                text={item?.narrative ?? ''}
                onSaveChanges={handleNarrativeChanged}
                placeholder={t('storyboard.editor.narrative_placeholder')}
                disableEditing={disableEditing}
                collapsable={!assistantItem}
                isCollapsed={isCollapsed}
                checkbox={showDifference?.narrative && assistantItem && (
                  <RadioButton
                    variant="primary"
                    checked={Boolean(!assistantSelected?.narrative)}
                    onChange={handleSelectOriginal('narrative')}
                    className={styles.radioBtn}
                  />
                )}
              />
            </div>
          )}
          {showDifference?.narrative && (
            <div className={styles.containerWithSelection}>
              <TextComponent
                title={!item && t('storyboard.editor.narrative_name')}
                text={assistantItem?.narrative ?? ''}
                placeholder={t('storyboard.editor.narrative_placeholder')}
                disableEditing={true}
                collapsable={false}
                variant="secondary"
                compareTo={item?.narrative}
                checkbox={item && (
                  <RadioButton
                    variant="secondary"
                    checked={Boolean(assistantSelected?.narrative)}
                    onChange={handleSelectAssistant('narrative')}
                    className={styles.radioBtn}
                  />
                )}
              />
            </div>
          )}
        </div>
        <div className={styles.container}>
          {item && (
            <div className={styles.containerWithSelection}>
              <TextComponent
                title={t('storyboard.editor.script_name')}
                countLength={true}
                text={item?.script ?? ''}
                onSaveChanges={handleScriptChanged}
                placeholder={t('storyboard.editor.script_placeholder')}
                disableEditing={disableEditing}
                collapsable={!assistantItem}
                isCollapsed={isCollapsed}
                checkbox={showDifference?.script && assistantItem && (
                  <RadioButton
                    variant="primary"
                    checked={!assistantSelected.script}
                    onChange={handleSelectOriginal('script')}
                    className={styles.radioBtn}
                  />
                )}
              />
            </div>
          )}
          {showDifference?.script && (
            <div className={styles.containerWithSelection}>
              <TextComponent
                title={!item && t('storyboard.editor.script_name')}
                countLength={true}
                text={assistantItem?.script ?? ''}
                placeholder={t('storyboard.editor.script_placeholder')}
                disableEditing={true}
                collapsable={false}
                compareTo={item?.script}
                variant="secondary"
                checkbox={item && (
                  <RadioButton
                    variant="secondary"
                    checked={assistantSelected.script}
                    onChange={handleSelectAssistant('script')}
                    className={styles.radioBtn}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
      {comparisonMode && !assistantItem && (
        <div className={cx(styles.contentWrapper, styles.noContent)}>
          <RadioButton
            variant="secondary"
            checked={!assistantItem && !assistantSelected}
            onChange={handleSelectAssistant(null)}
            className={styles.radioBtn}
          />
          <p>{t('storyboard.editor.assistant.old_scene_removed')}</p>
        </div>
      )}
    </div>
  );
};

export default GeneratedSceneItem;
